import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useConfirm } from '../../lib/confirm'
import { formatDateTime, titleCase } from '../../lib/format'
import type { ApprovalRequest } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import { CheckIcon } from '../../components/icons'

interface ApprovalsResponse { ok: boolean; items: ApprovalRequest[]; status: string }

const TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
]

export default function Approvals() {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [status, setStatus] = useState('pending')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'approvals', status],
    queryFn: () => api.get<ApprovalsResponse>(`/ops/approvals?status=${status}`),
  })

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/ops/approvals/${id}/approve`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'approvals'] }),
  })
  const reject = useMutation({
    mutationFn: (id: string) => api.post(`/ops/approvals/${id}/reject`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'approvals'] }),
  })

  const items = data?.items ?? []
  const busyId = approve.variables ?? reject.variables

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Approvals</h1>
          <p className="page-head__sub">Review pending changes that need a second sign-off.</p>
        </div>
      </div>

      {(approve.isError || reject.isError) && (
        <Alert tone="danger">
          {(approve.error instanceof ApiError && approve.error.message)
            || (reject.error instanceof ApiError && reject.error.message)
            || 'Could not update that request.'}
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Requests"
          action={(
            <div className="tab-scroll" style={{ gap: 4 }}>
              {TABS.map((t) => (
                <Button key={t.value} size="sm" variant={status === t.value ? 'primary' : 'ghost'}
                        onClick={() => setStatus(t.value)}>
                  {t.label}
                </Button>
              ))}
            </div>
          )}
        />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<CheckIcon size={22} />} title="Nothing here" body="Requests in this state will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Kind</th><th>Summary</th><th>Requested by</th><th>Requested</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600 }}>{titleCase(r.kind)}</td>
                      <td style={{ maxWidth: 320 }}>{r.summary || '—'}</td>
                      <td>{r.requested_by_name || '—'}</td>
                      <td>{formatDateTime(r.requested_at)}</td>
                      <td><StatusBadge status={r.status} /></td>
                      <td className="table__num">
                        {r.status === 'pending' && r.id && (
                          <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                            <Button size="sm" loading={approve.isPending && busyId === r.id}
                                    onClick={async () => {
                                      if (await confirm({ title: 'Approve this request?', message: r.summary })) {
                                        approve.mutate(r.id!)
                                      }
                                    }}>
                              Approve
                            </Button>
                            <Button size="sm" variant="danger" loading={reject.isPending && busyId === r.id}
                                    onClick={async () => {
                                      if (await confirm({ title: 'Reject this request?', message: r.summary, destructive: true })) {
                                        reject.mutate(r.id!)
                                      }
                                    }}>
                              Reject
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
